/**
 * URL Validation Utilities
 * 
 * Provides security-focused URL validation and safe opening functionality
 * for citation links to prevent security vulnerabilities.
 */

/**
 * Validates if a string is a well-formed URL
 */
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Checks if URL is a safe web link (http/https)
 */
export const isWebLink = (url: string): boolean => {
  if (!isValidUrl(url)) return false;
  
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Safely opens a URL in a new tab with security measures
 */
export const safelyOpenUrl = (url: string): boolean => {
  if (!isWebLink(url)) {
    console.warn('Invalid or unsafe URL provided:', url);
    return false;
  }

  try {
    // Open with security attributes to prevent potential attacks
    window.open(url, '_blank', 'noopener,noreferrer');
    return true;
  } catch (error) {
    console.error('Failed to open URL:', error);
    return false;
  }
};

/**
 * Analytics helper for tracking citation link clicks
 */
export const trackCitationClick = (citation: { text: string; url?: string }) => {
  try {
    // Check if dataLayer exists (Google Analytics)
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'citation_click',
        citation_text: citation.text,
        citation_url: citation.url || '',
      });
    }
  } catch (error) {
    console.warn('Failed to track citation click:', error);
  }
};